import { createBrowserRouter, Navigate, redirect, type LoaderFunction } from 'react-router';
import { RootErrorBoundary } from './components/error-boundary.tsx';

/** Loading spinner for lazy route hydration */
function RouteLoading() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-navy-50 dark:bg-navy-950">
      <div className="h-6 w-6 animate-spin rounded-full border-2 border-navy-300 border-t-emerald-500" />
    </div>
  );
}

/**
 * Auth guard — redirects to /login if no access token found.
 * Full session validation happens via API; this is a quick client-side check.
 */
const requireAuth: LoaderFunction = async () => {
  const token = localStorage.getItem('accessToken');
  if (!token) throw redirect('/login');
  return null;
};

/** Guest-only guard — redirects authenticated users to dashboard */
const requireGuest: LoaderFunction = async () => {
  const token = localStorage.getItem('accessToken');
  if (token) throw redirect('/dashboard');
  return null;
};

// Route definitions — structured per feature module
export const router = createBrowserRouter([
  // ── Public routes (guest-only) ──
  {
    path: '/login',
    loader: requireGuest,
    errorElement: <RootErrorBoundary />,
    hydrateFallbackElement: <RouteLoading />,
    lazy: () => import('./features/auth/routes.tsx'),
  },
  {
    path: '/login/magic-link',
    loader: requireGuest,
    lazy: () => import('./features/auth/magic-link-request.tsx'),
  },
  {
    path: '/login/magic-link/:token',
    lazy: () => import('./features/auth/magic-link-verify.tsx'),
  },
  {
    path: '/login/mfa',
    lazy: () => import('./features/auth/mfa-challenge.tsx'),
  },
  {
    path: '/register',
    loader: requireGuest,
    lazy: () => import('./features/auth/register.tsx'),
  },
  {
    path: '/register/:token',
    loader: requireGuest,
    lazy: () => import('./features/auth/accept-invitation.tsx'),
  },
  {
    path: '/forgot-password',
    loader: requireGuest,
    lazy: () => import('./features/auth/forgot-password.tsx'),
  },
  {
    path: '/reset-password/:token',
    lazy: () => import('./features/auth/reset-password.tsx'),
  },
  {
    path: '/verify-email/:token',
    lazy: () => import('./features/auth/verify-email.tsx'),
  },
  {
    path: '/verify-email-required',
    lazy: () => import('./features/auth/verify-email-required.tsx'),
  },

  // ── Logged-out confirmation page ──
  {
    path: '/logged-out',
    lazy: () => import('./features/auth/logged-out.tsx'),
  },

  // ── Protected routes (require auth) ──
  {
    path: '/',
    loader: requireAuth,
    errorElement: <RootErrorBoundary />,
    hydrateFallbackElement: <RouteLoading />,
    children: [
      // Redirect root to dashboard
      { index: true, element: <Navigate to="/dashboard" replace /> },
      { path: 'dashboard', lazy: () => import('./features/dashboard/routes.tsx') },

      // Onboarding wizard (post-registration)
      { path: 'onboarding', lazy: () => import('./features/onboarding/routes.tsx') },

      // Phase 1 — routes wired progressively
      { path: 'clients', lazy: () => import('./features/clients/routes.tsx') },
      {
        path: 'clients/:id',
        lazy: () => import('./features/clients/client-detail.tsx'),
      },
      { path: 'categories', lazy: () => import('./features/categories/routes.tsx') },
      { path: 'documents', lazy: () => import('./features/documents/routes.tsx') },
      { path: 'tax-periods', lazy: () => import('./features/tax-periods/routes.tsx') },
      { path: 'notifications', lazy: () => import('./features/notifications/routes.tsx') },
      { path: 'analytics', lazy: () => import('./features/analytics/routes.tsx') },
      { path: 'settings', lazy: () => import('./features/settings/routes.tsx') },
      { path: 'settings/security', lazy: () => import('./features/settings/security.tsx') },

      // Catch-all 404 inside authenticated area
      { path: '*', lazy: () => import('./features/not-found.tsx') },
    ],
  },

  // ── Global catch-all 404 ──
  {
    path: '*',
    errorElement: <RootErrorBoundary />,
    lazy: () => import('./features/not-found.tsx'),
  },
]);
